import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import moment from "moment";
import PeopleIcon from "@mui/icons-material/People";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EuroIcon from "@mui/icons-material/Euro";
import GroupIcon from "@mui/icons-material/Group";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DescriptionIcon from "@mui/icons-material/Description";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import {
  ENV_VERIFIED_TRAINER,
  getActiveBrandConfig,
  getActiveClientBrandConfig,
  getActivePrestationBrandConfig,
  getPathClientBrandConfig,
  isBrandActive,
} from "../../config/environment.config";
import { capitalizeFirstLetter } from "../../Utils/Function";
import type Teachr from "../../Objects/models/Teachr";
import type { StatItem } from "../../Components/App/Home/StatCard";
import type { SponsorshipMetric } from "../../Components/App/Home/SponsorshipCard";
import type { SponsorshipMetrics } from "../../Components/App/Home/Resume";
import { userSetCurrentProcuration } from "../../Redux/Actions/UserActions";
import { PROCURATION_AE, PROCURATION_SAP } from "../../Helpers/ProcurationType";
// import useTailwindBrand from "../../Theme/useTailwindBrand";
import { getBrandConfig } from "../../config/brandConfig";

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
  action?: () => void;
  subSteps?: { total: number; completed: number };
  children?: OnboardingStep[];
  locked?: boolean;
}

export type DataChartType = {
  courses?: { startmonth: string; fees: string; teachr_price: string; amount: string; amount_vt: string, duration: string; remuneration?: string }[];
  total?: number;
  totalVt?: number;
  totalAmount?: number;
  totalAmountVt?: number;
  totalDuration?: number;
  /** Commissions enregistrées (fees × heures) sur la période et depuis le début : prix total TTC = montant + commissions */
  fees?: number;
  totalFees?: number;
  /** Rémunération telle que facturée (part HT + TVA enregistrées) sur la période et depuis le début */
  remuneration?: number;
  totalRemuneration?: number;
};

export type SponsorshipData = {
  sponsorship?: any[];
  totalSponsors?: number;
  totalFees?: number;
  totalMonthlyFees?: number;
  hasSponsors?: boolean;
  declaronsSponsored?: number;
} | null | undefined;

export const LEVEL_CONFIG: Record<number, { name: string; emoji: string }> = {
  1: { name: "Explorateur", emoji: "👣" },
  2: { name: "Médaille de Bronze", emoji: "🥉" },
  3: { name: "Médaille d'Argent", emoji: "🥈" },
  4: { name: "Médaille d'Or", emoji: "🥇" },
  5: { name: "Diamant", emoji: "💎" },
  6: { name: "Diamant +", emoji: "💎" },
  7: { name: "Diamant ++", emoji: "💎" },
};

export const MAX_LEVEL = 7;

export function getCurrentLevel(teachr?: Teachr | null): number {
  const lvl = parseInt(teachr?.level ?? "1");
  return isNaN(lvl) ? 1 : Math.min(Math.max(lvl, 1), MAX_LEVEL);
}

const getBrandColorClasses = () => {
  const brand = getActiveBrandConfig();
  switch (brand) {
    case "verified-trainer":
      return {
        bgGradient: "bg-gradient-to-br from-vt-background to-vt-light",
        bgGradient1: "bg-gradient-to-r from-green-50 to-indigo-50 border-green-200",
        bgCard: "bg-vt-background",
        border: "border-vt-primary",
        text: "text-vt-primary",
        textSecondary: "text-vt-secondary",
        iconBg: "bg-vt-primary",
        accent: "bg-vt-accent",
        success: "bg-vt-success",
        warning: "bg-vt-warning",
      };
    case "declarons":
      return {
        bgGradient:
          "bg-gradient-to-br from-declarons-background to-declarons-light",
        bgGradient1:
          "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200",
        bgCard: "bg-declarons-background",
        border: "border-declarons-primary",
        text: "text-declarons-primary",
        textSecondary: "text-declarons-secondary",
        iconBg: "bg-declarons-primary",
        accent: "bg-declarons-accent",
        success: "bg-declarons-success",
        warning: "bg-declarons-warning",
      };
    default:
      return {
        bgGradient: "bg-gradient-to-br from-brand-background to-brand-light",
        bgGradient1:
          "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200",
        bgCard: "bg-brand-background",
        border: "border-brand-primary",
        text: "text-brand-primary",
        textSecondary: "text-brand-secondary",
        iconBg: "bg-brand-primary",
        accent: "bg-brand-accent",
        success: "bg-brand-success",
        warning: "bg-brand-warning",
      };
  }
};


export function getSponsorshipMetrics(sponsorship: any): SponsorshipMetrics {
  if (!sponsorship?.sponsorship?.length) {
    return {
      totalSponsors: 0,
      totalFees: 0,
      totalMonthlyFees: 0,
      hasSponsors: false,
    };
  }
  return {
    totalSponsors: sponsorship.totalSponsors ?? 0,
    totalFees: sponsorship.totalFees ?? 0,
    totalMonthlyFees: sponsorship.totalMonthlyFees ?? 0,
    hasSponsors: true,
  };
}

export function buildStatistics(params: {
  totalStudents?: number;
  totalStudentsNoActive?: number;
  upcomingCourses?: number;
  dataChart?: { total?: number; totalVt?: number; totalFees?: number };
}): StatItem[] {
  const {
    totalStudents = 0,
    totalStudentsNoActive = 0,
    upcomingCourses = 0,
    dataChart,
  } = params;

  const { colors } = getBrandConfig();
  const pluralS = (n: number) => (n > 1 ? "s" : "");
  const clientLabel = getActiveClientBrandConfig(pluralS(totalStudents));
  const clientLabelNoActive = getActiveClientBrandConfig(
    pluralS(totalStudentsNoActive)
  );
  const prestationLabel = getActivePrestationBrandConfig(pluralS(upcomingCourses));

  return [
    {
      label: `📊 ${capitalizeFirstLetter(clientLabel)} validé${pluralS(totalStudents)}`,
      value: totalStudents,
      subTitle: `🧑‍💻 ${capitalizeFirstLetter(clientLabel)} inscrit${pluralS(totalStudents)}`,
      icon: <PeopleIcon className="h-7 w-7 text-white" />,
      color: colors.primary,
      navigate: `/${getActiveBrandConfig()}/tableau-de-bord/${getPathClientBrandConfig()}`,
    },
    {
      label: `📝 ${capitalizeFirstLetter(clientLabelNoActive)} en attente`,
      value: totalStudentsNoActive,
      subTitle: `⏳ Inscription${pluralS(totalStudentsNoActive)} non finalisée${pluralS(totalStudentsNoActive)}`,
      icon: <PeopleIcon className="h-7 w-7 text-white" />,
      color: colors.warning,
      navigate: `/${getActiveBrandConfig()}/tableau-de-bord/${getPathClientBrandConfig()}`,
    },
    {
      label: `📅 ${capitalizeFirstLetter(prestationLabel)} à venir`,
      value: upcomingCourses,
      subTitle: `📈 ${capitalizeFirstLetter(prestationLabel)} de ce mois ${moment().format("MMMM")}`,
      icon: <ScheduleIcon className="h-7 w-7 text-white" />,
      color: colors.secondary,
      navigate: `/${getActiveBrandConfig()}/tableau-de-bord/${getActivePrestationBrandConfig()}`,
    },
    {
      label: isBrandActive(ENV_VERIFIED_TRAINER) ? "💶 Prix total déclaré" : "💶 Revenus totaux",
      // Verified Trainer : prix total TTC = rémunération + commissions enregistrées (totalVt n'est plus renvoyé par l'API)
      value: isBrandActive(ENV_VERIFIED_TRAINER)
        ? ((dataChart?.total ?? 0) + (dataChart?.totalFees ?? 0)).toFixed(2)
        : (dataChart?.total ?? 0).toFixed(2),
      subTitle: isBrandActive(ENV_VERIFIED_TRAINER)
        ? `💰 Rémunération : ${(dataChart?.total ?? 0).toFixed(2)} € depuis le début`
        : "💰 Calculé depuis le début",
      icon: <EuroIcon className="h-7 w-7 text-white" />,
      color: colors.success,
    },
  ];
}

export function buildSponsorshipCards(
  metrics: SponsorshipMetrics,
  primaryTextClass: string
): SponsorshipMetric[] {
  const brandConfig = getBrandConfig()
  return [
    {
      title: metrics.totalSponsors,
      icon: <GroupIcon className={`w-5 h-5 ${primaryTextClass}`} />,
      subtitle: ` Filleul${metrics.totalSponsors > 1 ? "s" : ""} actif${metrics.totalSponsors > 1 ? "s" : ""}`,
      className: primaryTextClass,
      color: brandConfig.colors.primary
    },
    {
      title: `${metrics.totalFees.toFixed(2)}€`,
      icon: <EuroIcon className="w-5 h-5 text-green-600" />,
      subtitle: "Total gagné",
      className: "text-green-800",
      color: '#166534'

    },
    {
      title: `${metrics.totalMonthlyFees.toFixed(2)}€`,
      icon: <TrendingUpIcon className="w-5 h-5 text-orange-600" />,
      subtitle: "Ce mois",
      className: "text-orange-800",
      color: '#9a3412'
    },
  ];
}

export function computeTotalHours(dataChart?: DataChartType | null): number {
  return (dataChart?.courses ?? []).reduce(
    (sum, c) => sum + parseFloat(c.duration || "0"), 0
  );
}

// NOTE: getStepsLevel2 à getStepsLevel7 et getStepsForLevel sont conservés
// dans useHomeLevels.tsx pour la prochaine version. Ils ne sont plus appelés.

function getSteps(
  teachr: Teachr | null | undefined,
  totalStudents: number,
  totalStudentsNoActive: number,
  navigate: ReturnType<typeof useNavigate>,
  dispatch: ReturnType<typeof useDispatch>,
  handleAddUser: () => void,
  handleDeclare: () => void,
): OnboardingStep[] {
  const hasStudents = totalStudents > 0;
  const hasStudentAdded = totalStudentsNoActive > 0 || hasStudents;
  const hasCompanyInfo = !!(teachr?.businessType && teachr?.RCS);
  const hasActive = !!(teachr?.isAE && teachr?.signSap && teachr?.isValidated)
  const hasIbanBic = !!(teachr?.iban && teachr?.bic);
  const hasDecSap = !!teachr?.RCS && !!teachr?.address && !!teachr?.proofSignSap
  const hasDeclaredCourse = !!(teachr?.isFirstCourse);
  const scoop = parseInt(teachr?.scoop as string) || 0;

  const proc = teachr?.procuration;
  const hasProcurationAE = !!teachr?.proofIsAE;
  const hasProcurationSap = !!teachr?.proofSignSap;
  // proc "10"=Coopérative, "20"=Procuration SAP, "30"=Procuration AE+SAP, "40"=Déclaration Nova
  const hasCooperativeComplete = proc === "10" && scoop >= 20 && !!teachr?.proofSignSap && !!teachr?.proofIdentity;
  const profileChoiceComplete = (proc === "10" || proc === "20" || proc === "30" || proc === "40" || !!teachr?.proofMandat) && !!teachr?.businessType;
  const atSignConditions = proc === "40" || (proc === "10" && hasCooperativeComplete) || (proc === "30" && hasProcurationAE) || (proc === "20" && hasProcurationSap);

  const c1 = profileChoiceComplete || hasActive ? 1 : 0;
  const c2 = hasActive ? 1
    : proc === "10" ? (hasCooperativeComplete ? 1 : 0)
      : proc === "20" ? (hasProcurationSap ? 1 : 0)
        : proc === "30" ? (hasProcurationAE || hasProcurationSap ? 1 : 0)
          : proc === "40" ? (hasDecSap ? 1 : 0)
            : 0;
  const c3 = atSignConditions || hasActive ? (!!teachr?.proofMandat || hasActive ? 1 : 0) : 0;
  const c4 = hasActive ? 1 : 0;

  const signConditionsSubSteps = {
    total: 4,
    completed: [
      !!teachr?.businessType || proc === "10" || proc === "20" || proc === "30" || proc === "40" || hasActive,
      hasCooperativeComplete || hasProcurationAE || hasProcurationSap || hasDecSap || hasActive,
      !!teachr?.proofMandat || hasActive,
      hasActive
    ].filter(Boolean).length,
  };
  const signConditionsComplete = signConditionsSubSteps.completed === signConditionsSubSteps.total;
  const goToProcuration = () => {
    // On dispatche le même code que celui stocké dans teachr.procuration (30 = AE,
    // 20 = SAP) : l'ancienne traduction vers 1/2 masquait le bug du hook pour les
    // formateurs passant par le tableau de bord, mais pas pour les nouveaux clients.
    const hasBusinessType = !!teachr?.businessType && String(teachr?.businessType).trim() !== "";
    const type = proc === "30"
      ? PROCURATION_AE
      : proc === "20"
        ? PROCURATION_SAP
        : hasBusinessType ? PROCURATION_SAP : PROCURATION_AE;
    dispatch(userSetCurrentProcuration(type));
    navigate(`/${getActiveBrandConfig()}/tableau-de-bord/procuration`);
  };

  const goToSignConditions = () => { navigate(`/${getActiveBrandConfig()}/tableau-de-bord/complete`, { state: { step: 3 } }) };

  const baseSteps: OnboardingStep[] = [
    {
      id: "student",
      title: `Ajoutez votre premier ${getActiveClientBrandConfig()}`,
      description: `Inscrivez un ${getActiveClientBrandConfig()} et attendez qu'il valide son inscription`,
      icon: hasStudents
        ? <span className="text-5xl">🧑‍🎓</span>
        : hasStudentAdded
          ? <span className="text-5xl">⏳</span>
          : <PersonAddIcon className="w-5 h-5 text-gray-400" />,
      completed: hasStudents,
      subSteps: { total: 2, completed: (hasStudentAdded ? 1 : 0) + (hasStudents ? 1 : 0) },
      children: [
        {
          id: "student-add",
          title: `Ajoutez votre premier ${getActiveClientBrandConfig()}`,
          description: `Cliquez sur « Commencer » pour ajouter un ${getActiveClientBrandConfig()}`,
          icon: hasStudentAdded
            ? <span className="text-4xl">🧑‍🎓</span>
            : <PersonAddIcon className="w-5 h-5 text-gray-400" />,
          completed: hasStudentAdded,
          action: () => handleAddUser(),
          subSteps: { total: 1, completed: hasStudentAdded ? 1 : 0 },
        },
        {
          id: "student-validate",
          title: `Votre ${getActiveClientBrandConfig()} valide son inscription`,
          description: `Votre ${getActiveClientBrandConfig()} doit remplir et valider le formulaire d'inscription. Relancez-le si nécessaire.`,
          icon: hasStudents
            ? <span className="text-4xl">✅</span>
            : <PersonAddIcon className="w-5 h-5 text-gray-400" />,
          completed: hasStudents,
          locked: !hasStudentAdded,
          action: () => navigate(`/${getActiveBrandConfig()}/tableau-de-bord/${getPathClientBrandConfig()}`),
          subSteps: { total: 1, completed: hasStudents ? 1 : 0 },
        },
      ],
    },
    {
      id: "iban",
      title: "Ajoutez votre IBAN et BIC",
      description: "Renseignez vos coordonnées bancaires afin de recevoir votre rémunération",
      icon: hasIbanBic ? <span className="text-5xl">🏦</span> : <AccountBalanceIcon className="w-5 h-5 text-gray-400" />,
      completed: hasIbanBic,
      action: () =>
        navigate(`/${getActiveBrandConfig()}/tableau-de-bord/parametre`, {
          state: { step: 2 },
        }),
      subSteps: { total: 1, completed: hasIbanBic ? 1 : 0 },
    },
    {
      id: "complete-profile",
      title: "Complétez votre profil",
      description:
        "Indiquez votre statut juridique, ajoutez une procuration si nécessaire, signez les conditions de service, puis validez votre compte afin de pouvoir commencer vos déclarations.",
      icon: c1 && c2 && c3 && c4 ? <span className="text-5xl">👤</span> : <PersonIcon className="w-5 h-5 text-gray-400" />,
      completed: c1 && c2 && c3 && c4 ? true : false,
      action: () => navigate(`/${getActiveBrandConfig()}/tableau-de-bord/complete`),
      subSteps: (() => {
        return { total: 4, completed: c1 + c2 + c3 + c4 };
      })(),
      children: (() => {
        const goToComplete = () =>
          navigate(`/${getActiveBrandConfig()}/tableau-de-bord/complete`);
        const child1: OnboardingStep = {
          id: "complete-profile-choice",
          title: "Statut juridique",
          description:
            "Configurez votre statut et vérifiez votre numéro SIRET pour activer toutes les fonctionnalités.",
          icon: profileChoiceComplete ? <span className="text-4xl">👤</span> : <PersonIcon className="w-5 h-5 text-gray-400" />,
          completed: profileChoiceComplete || hasActive,
          action: goToComplete,
          subSteps: { total: 1, completed: profileChoiceComplete ? 1 : 0 },
        };
        let child2: OnboardingStep;
        if (proc === "10") {
          child2 = {
            id: "cooperative",
            title: "Coopérative",
            description: "Complétez votre adhésion à la coopérative : signez le règlement intérieur, effectuez votre paiement et activez votre compte coopérative.",
            icon: hasCooperativeComplete || hasActive ? <span className="text-4xl">🤝</span> : <AssignmentIcon className="w-5 h-5 text-gray-400" />,
            completed: hasCooperativeComplete || hasActive,
            locked: !profileChoiceComplete,
            action: goToComplete,
            subSteps: { total: 1, completed: hasCooperativeComplete || hasActive ? 1 : 0 },
          };
        } else if (proc === "20" && !isBrandActive(ENV_VERIFIED_TRAINER)) {
          child2 = {
            id: "procuration-sap",
            title: "Procuration SAP (Service à la personne)",
            description: "Vous avez choisi la procuration SAP (Service à la personne) : finalisez-la pour que nous puissions effectuer la déclaration à votre place.",
            icon: hasProcurationSap || hasActive ? <span className="text-4xl">📑</span> : <AssignmentIcon className="w-5 h-5 text-gray-400" />,
            completed: hasProcurationSap || hasActive,
            action: goToComplete,
            locked: !profileChoiceComplete,
            subSteps: { total: 1, completed: (hasProcurationSap || hasActive) ? 1 : 0 },
          };
        } else if (proc === "30" && !isBrandActive(ENV_VERIFIED_TRAINER)) {
          child2 = {
            id: "procuration-ae-sap",
            title: "Procuration AE et SAP (Auto-entrepreneur et Service à la personne)",
            description: "Vous avez choisi la procuration AE et SAP : finalisez-la afin d’obtenir votre statut d’auto-entrepreneur et activer votre déclaration SAP.",
            icon: hasProcurationAE || hasActive ? <span className="text-4xl">📑</span> : <AssignmentIcon className="w-5 h-5 text-gray-400" />,
            completed: hasProcurationAE || hasActive,
            action: goToProcuration,
            locked: !profileChoiceComplete,
            subSteps: { total: 1, completed: (hasProcurationAE || hasActive) ? 1 : 0 },
          };
        } else if (proc === "40" && !isBrandActive(ENV_VERIFIED_TRAINER)) {
          child2 = {
            id: "declaration-nova",
            title: "Déclaration Nova (Service à la Personne)",
            description: "Remplissez et envoyez facilement votre déclaration SAP pour bénéficier des services à la personne.",
            icon: hasDecSap ? <span className="text-4xl">📑</span> : <AssignmentIcon className="w-5 h-5 text-gray-400" />,
            completed: hasDecSap || hasActive,
            locked: !profileChoiceComplete,
            action: goToComplete,
            subSteps: { total: 1, completed: hasDecSap ? 1 : 0 },
          };
        } else {
          child2 = {
            id: "procuration-choice",
            title: "Procuration & déclaration SAP",
            description: "Selon votre situation, vous pourrez rejoindre la coopérative, obtenir une procuration SAP, une procuration AE et SAP, ou effectuer une déclaration Nova. Ce choix déterminera votre mode de déclaration.",
            icon: <AssignmentIcon className="w-5 h-5 text-gray-400" />,
            completed: false || hasActive,
            locked: !profileChoiceComplete,
            action: goToComplete,
            subSteps: { total: 1, completed: 0 },
          };
        }
        const child3: OnboardingStep = {
          id: "sign-conditions",
          title: "Signez les conditions",
          description: "Signez les conditions de service.",
          icon: !!teachr?.proofMandat ? <span className="text-4xl">📝</span> : <DescriptionIcon className="w-5 h-5 text-gray-400" />,
          completed: !!teachr?.proofMandat || hasActive,
          action: goToSignConditions,
          locked: proc === "40" ? !hasDecSap : proc === "10" ? !hasCooperativeComplete : !(hasProcurationAE || hasProcurationSap),
          subSteps: { total: 1, completed: !!teachr?.proofMandat || hasActive ? 1 : 0 },
        };
        const child4: OnboardingStep = {
          id: "valid-compte",
          title: "Validez votre compte",
          description:
            "Maintenant faudra valider votre compte avant de continuer",
          icon: hasActive ? <span className="text-4xl">✅</span> : <DescriptionIcon className="w-5 h-5 text-gray-400" />,
          completed: signConditionsComplete && hasActive,
          action: goToSignConditions,
          locked: !teachr?.proofMandat,
          subSteps: { total: 1, completed: hasActive ? 1 : 0 },
        };
        return [child1, child2, child3, child4];
      })(),
    },
    {
      id: "course",
      title: "Déclarez votre premier cours",
      description: `Déclarez votre premier cours avec votre premier ${getActiveClientBrandConfig()}`,
      icon: hasDeclaredCourse ? <span className="text-5xl">📚</span> : <MenuBookIcon className="w-5 h-5" />,
      completed: hasDeclaredCourse,
      locked: hasActive && hasIbanBic && hasStudents ? false : true,
      action: () => handleDeclare(),
      // navigate(`/${getActiveBrandConfig()}/tableau-de-bord/${getActivePrestationBrandConfig()}/declare`),
      subSteps: {
        total: 1,
        completed: hasDeclaredCourse ? 1 : 0,
      },
    },
  ];
  // if (isBrandActive(ENV_VERIFIED_TRAINER)) {
  //   const businessInfoStep: OnboardingStep = {
  //     id: "business-info",
  //     title: "Informations d'entreprise",
  //     description: "Renseignez votre forme de société et votre numéro SIRET",
  //     icon: hasCompanyInfo ? <span className="text-5xl">🏢</span> : <BusinessCenterIcon className="w-5 h-5 text-gray-400" />,
  //     completed: hasCompanyInfo,
  //     action: () => navigate(`/${getActiveBrandConfig()}/tableau-de-bord/parametre`, { state: { step: 4 } }),
  //     subSteps: { total: 1, completed: hasCompanyInfo ? 1 : 0 },
  //   };
  //   return [baseSteps[0], baseSteps[1], businessInfoStep, baseSteps[3]];
  // }
  return baseSteps;
}
export function getOnboardingProgress(steps: OnboardingStep[]) {
  const totalSubSteps = steps.reduce(
    (acc, s) => acc + (s.subSteps?.total ?? 1),
    0
  );
  const completedSubSteps = steps.reduce(
    (acc, s) => acc + (s.subSteps?.completed ?? 0),
    0
  );
  const overallProgress =
    totalSubSteps > 0 ? Math.round((completedSubSteps / totalSubSteps) * 100) : 0;
  return { totalSubSteps, completedSubSteps, overallProgress };
}
const useHome = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const brandColors = getBrandColorClasses();

  const getOnboardingSteps = React.useCallback((
    handleAddUser: () => void,
    handleDeclare: () => void,
    teachr?: Teachr | null,
    totalStudents: number = 0,
    totalStudentsNoActive: number = 0,
  ) => {
    const currentLevelNum = getCurrentLevel(teachr);
    // Niveau unique (niveau 1) - les niveaux 2-7 seront réintégrés à la prochaine version
    const steps = getSteps(teachr, totalStudents, totalStudentsNoActive, navigate, dispatch, handleAddUser, handleDeclare);
    const { overallProgress } = getOnboardingProgress(steps);
    return { steps, overallProgress, currentLevelNum };
  },
    [navigate, dispatch]
  );

  return {
    brandColors,
    // getProfileCompletion,
    getSponsorshipMetrics,
    buildStatistics,
    buildSponsorshipCards,
    getOnboardingSteps,
  };
};

export default useHome;
